/**
 * Read files client-side for knowledge ingestion. Plain-text files are read as UTF-8 in the browser;
 * binary docs (PDF/DOCX/DOC) are read as base64 and extracted server-side (fileExtract).
 */
export const TEXT_FILE_EXTS = ['.txt', '.md', '.markdown', '.csv', '.json'];
/** Documents the server can extract text from (base64 upload path). */
export const DOC_FILE_EXTS = ['.pdf', '.docx', '.doc', '.pptx', '.xlsx'];
/** Images the server reads via the vision model (OCR + description) — same base64 upload path as docs. */
export const IMAGE_FILE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
/** Everything the KB upload accepts (text read locally + docs/images extracted server-side). */
export const KB_UPLOAD_EXTS = [...TEXT_FILE_EXTS, ...DOC_FILE_EXTS, ...IMAGE_FILE_EXTS];
export const MAX_TEXT_FILE_BYTES = 1_000_000; // 1 MB
export const MAX_DOC_FILE_BYTES = 10_000_000; // 10 MB (matches the per-scope KB cap)

/** True for plain-text files we can read directly in the browser. */
export function isTextFile(name: string, type?: string): boolean {
  const lower = (name || '').toLowerCase();
  return (type || '').startsWith('text/') || TEXT_FILE_EXTS.some((e) => lower.endsWith(e));
}

/** True for binary documents the server extracts text from. */
export function isDocFile(name: string, type?: string): boolean {
  const lower = (name || '').toLowerCase();
  const t = (type || '').toLowerCase();
  return t.includes('pdf') || t.includes('word') || t.includes('presentation') || t.includes('spreadsheet')
    || DOC_FILE_EXTS.some((e) => lower.endsWith(e));
}

/** True for images the server reads via the vision model (uploaded the same base64 way as documents). */
export function isImageFile(name: string, type?: string): boolean {
  const lower = (name || '').toLowerCase();
  return (type || '').toLowerCase().startsWith('image/') || IMAGE_FILE_EXTS.some((e) => lower.endsWith(e));
}

/** Read a File as base64 (no data: prefix) for server-side extraction. Rejects if too large. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_DOC_FILE_BYTES) {
      reject(new Error(`"${file.name}" is too large (max 10 MB).`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]*;base64,/, ''));
    reader.onerror = () => reject(new Error(`Could not read "${file.name}".`));
    reader.readAsDataURL(file);
  });
}

/** Read a File as UTF-8 text (rejects if too large or unreadable). */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_TEXT_FILE_BYTES) {
      reject(new Error(`"${file.name}" is too large (max 1 MB).`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Could not read "${file.name}".`));
    reader.readAsText(file);
  });
}
